import { CSmartTable } from '@coreui/react-pro'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { useEffect, useRef, useState } from 'react'
import { EDVCParent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import BookedEventLogsDrawer from 'src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer'
import { getCPDataWithParams } from 'src/clinic/services'
import ClinicBrandPayoutReportsViewDetails from 'src/clinic/views/clinicBrandPayoutReports/components/ClinicBrandPayoutReportsViewDetails'
import PayoutDistributionReportFeeDetails from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutDistributionReportFeeDetails'
import PayoutDistributionReportModelDetails from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutDistributionReportModelDetails'
import PayoutReportSummary from 'src/clinic/views/clinicBrandPayoutReports/components/PayoutReportSummary'
import {
  BrandPayoutStyledSmartTable,
  PayoutReportsDateRangeBox,
  PayoutReportsPaginationBar,
  PayoutReportsToolbar,
} from 'src/clinic/views/clinicBrandPayoutReports/styledComponents'
import { buildTherapistPayoutReportsExcel } from 'src/clinic/views/clinicBrandPayoutReports/util'
import ExportPayoutReportsDialogWithButton from 'src/common/components/payoutReports/ExportPayoutReportsDialogWithButton'
import { GenericHeaderBackButton } from 'src/consumer/components/ActivityPlansPage/MyPlanComponents'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import {
  MONTHLY_PAYMENT_CYCLE,
  getPaymentCycleShortcuts,
  getPreviousPaymentCycleDateRange,
  isPaymentCycleCalendarMonth,
  safeDayjsTz,
} from 'src/clinic/views/clinicBrandPayoutReports/utils/paymentCycleDateRanges'
import {
  BOOKED_EVENT_MODES,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  EVENT_TYPES_ARRAY,
  MOMENT_FORMATS,
  PAYMENT_METHOD_KV_MAP,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  getCSmartTableColumnData,
  getGenderFromGenderId,
  isValidNumber,
} from 'src/consumer/helpers/utilFunctions'
import { PageMainContentContainer } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  getUniqueClinicBrandsFromMentorAssociatedClinics,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { PaymentConfigMenuWithDialog } from 'src/supportPortal/components/common/paymentConfig/PaymentConfigExample'
import { checkIfReportNeedsReconsiliationForTherapists } from 'src/mentor/components/TeacherPayoutReports/utils'

const DEFAULT_EVENT_TYPE = EVENT_TYPE_THERAPY_SESSION
const DEFAULT_FILTERS = { eventType: DEFAULT_EVENT_TYPE }
const SELECT_MENU_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  menu: (base) => ({ ...base, zIndex: 1600 }),
}

function sanitizeFilterQuery(filters = {}) {
  return Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {})
}

export default function TeacherPayoutReports({
  showSummaryReport = false,
  eventType,
  clinicAssociationData,
  showAdminActions = false,
  hideBackButton = false,
  reportListApiSignature = '/api/secure/teacher/payout-reports/paginated',
  summaryReportApiSignature = '/api/secure/teacher/payout-reports/summary',
  reportsListingApiParams,
  summaryReportApiParams,
  useLocalDateFormatForApiCalls = false,
  bookedEventLogApiUrl = '/api/secure/teacher/booked-event-logs/paginated',
  allEventTypes,
  paymentConfig,
  onPaymentConfigSaved,
  practitionerContact,
}) {
  const clinicBrands = getUniqueClinicBrandsFromMentorAssociatedClinics(clinicAssociationData)
  const hasInitializedFiltersRef = useRef(false)
  const hasInitializedPaymentConfigRef = useRef(false)
  const hasLockedClinicBrand = !!reportsListingApiParams?.['clinic-brand-id']
  const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  // Ensure initial dates are always valid
  const getInitialStartDate = () => {
    const date = getPreviousPaymentCycleDateRange(paymentConfig).start
    if (date.isValid()) {
      return date
    }
    return dayjs(new Date()).subtract(1, 'month').startOf('month')
  }

  const getInitialEndDate = () => {
    const date = getPreviousPaymentCycleDateRange(paymentConfig).end
    if (date.isValid()) {
      return date
    }
    return dayjs(new Date()).subtract(1, 'month').endOf('month')
  }

  const [reportDate, setReportDate] = useState(() => getInitialStartDate())
  const [reportEndDate, setReportEndDate] = useState(() => getInitialEndDate())
  const [listData, setListData] = useState([])
  const [paginationData, setPaginationData] = useState({ page: 0, totalPages: 0 })
  const [loadingPageData, setLoadingPageData] = useState(false)
  const [summaryReport, setSummaryReport] = useState()
  const [loadingSummaryReport, setLoadingSummaryReport] = useState(false)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [patientNumberInput, setPatientNumberInput] = useState('')
  const [showBookedEventLogsDialog, setShowBookedEventLogsDialog] = useState(false)
  const [selectedBookedEvent, setSelectedBookedEvent] = useState(null)
  const activeEventType = filters?.eventType || eventType || DEFAULT_EVENT_TYPE
  const isNonClinicalOnly = filters?.nonClinicalEvents === 'true'
  const paymentConfigSignature = [
    paymentConfig?.paymentCycle || MONTHLY_PAYMENT_CYCLE,
    paymentConfig?.paymentCycleLengthInDays || '',
    paymentConfig?.paymentCycleStartDayOfMonth || '',
  ].join('|')
  const paymentCycleShortcuts = getPaymentCycleShortcuts(paymentConfig, { count: 4 })
  const usesCalendarMonthCycle = isPaymentCycleCalendarMonth(paymentConfig)

  function updateFilter(filterName, nextValue) {
    setFilters((prev) => {
      const updatedFilters = { ...(prev || {}) }

      if (filterName === 'eventType') {
        updatedFilters.eventType = nextValue || DEFAULT_EVENT_TYPE
      } else if (
        nextValue === undefined ||
        nextValue === null ||
        nextValue === '' ||
        nextValue === 'all'
      ) {
        delete updatedFilters[filterName]
      } else {
        updatedFilters[filterName] = nextValue
      }

      if (filterName === 'nonClinicalEvents' && nextValue === 'true') {
        delete updatedFilters['clinic-brand-id']
      }

      if (!updatedFilters.eventType) {
        updatedFilters.eventType = DEFAULT_EVENT_TYPE
      }

      return updatedFilters
    })
  }

  const activeFilterChips = [
    ...(activeEventType !== DEFAULT_EVENT_TYPE
      ? [
          {
            key: 'eventType',
            label: `Service: ${
              EVENT_TYPES_ARRAY.find((item) => item?.id === activeEventType)?.title ||
              activeEventType
            }`,
            onDelete: () => updateFilter('eventType', DEFAULT_EVENT_TYPE),
          },
        ]
      : []),
    ...(!hasLockedClinicBrand && filters?.['clinic-brand-id']
      ? [
          {
            key: 'clinic-brand-id',
            label: `Clinic: ${
              clinicBrands?.find((brand) => brand?.id === filters?.['clinic-brand-id'])?.title || ''
            }`,
            onDelete: () => updateFilter('clinic-brand-id', null),
          },
        ]
      : []),
    ...(filters?.['patient-number']
      ? [
          {
            key: 'patient-number',
            label: `Patient: ${filters?.['patient-number']}`,
            onDelete: () => {
              setPatientNumberInput('')
              updateFilter('patient-number', null)
            },
          },
        ]
      : []),
    ...(filters?.nonClinicalEvents
      ? [
          {
            key: 'nonClinicalEvents',
            label: filters?.nonClinicalEvents === 'true' ? 'Non-clinical only' : 'Clinical only',
            onDelete: () => updateFilter('nonClinicalEvents', null),
          },
        ]
      : []),
    ...(filters?.markedCompletedEvents
      ? [
          {
            key: 'markedCompletedEvents',
            label:
              filters?.markedCompletedEvents === 'true'
                ? 'Marked completed only'
                : 'Not marked completed only',
            onDelete: () => updateFilter('markedCompletedEvents', null),
          },
        ]
      : []),
  ]
  const isAnyFilterApplied = activeFilterChips.length > 0

  async function getReports({
    page = 0,
    reportDateMoment = safeDayjsTz(),
    reportEndDateMoment = safeDayjsTz(),
    filterQuery,
  }) {
    const resp = await getCPDataWithParams(reportListApiSignature, {
      page,
      eventType: activeEventType,
      startTime: reportDateMoment.format(
        useLocalDateFormatForApiCalls
          ? MOMENT_FORMATS.javaLocalDateFormatNew
          : MOMENT_FORMATS.javaInstantFormatNew,
      ),
      endTime: reportEndDateMoment.format(
        useLocalDateFormatForApiCalls
          ? MOMENT_FORMATS.javaLocalDateFormatNew
          : MOMENT_FORMATS.javaInstantFormatNew,
      ),
      ...(filterQuery || {}),
      ...(reportsListingApiParams || {}),
    })
    if (resp.status === 200) {
      const data = resp.data.content
      const paginationData = resp?.data?.pageable

      const pageNumber = paginationData?.pageNumber
      const totalPages = resp?.data?.totalPages

      setListData(data)
      setPaginationData((prev) => ({ ...prev, page: pageNumber, totalPages: totalPages }))
    }
  }

  async function getSummaryReports({ reportDateMoment, reportEndDateMoment, filterQuery }) {
    setLoadingSummaryReport(true)
    let stTime = reportDateMoment.startOf('day').utc().format(MOMENT_FORMATS.javaInstantFormatNew)
    let eTime = reportEndDateMoment.endOf('day').utc().format(MOMENT_FORMATS.javaInstantFormatNew)
    // if (reportDateMoment.isSame(reportEndDateMoment, 'day')) {
    //   eTime = reportDateMoment
    //
    //     .endOf('day')
    //     .utc()
    //     .format(MOMENT_FORMATS.javaInstantFormatNew)
    // }
    const resp = await getCPDataWithParams(summaryReportApiSignature, {
      eventType: activeEventType,
      startTime: stTime,
      endTime: eTime,
      ...(filterQuery || {}),
      ...(summaryReportApiParams || {}),
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const reportData = data?.['payout-distribution-summary-for-mentor']
        setSummaryReport(reportData)
      }
    }
    setLoadingSummaryReport(false)
  }

  function getAndSetAppropriatePaginationData(page, sort, pageSize) {
    let pg = page
    let srt = sort
    let pgSize = pageSize
    if (!isValidNumber(pg)) {
      pg = paginationData.page
    }

    setPaginationData((prev) => ({
      ...prev,
      page: pg,
      pageSize: pgSize,
    }))

    return { page: pg, sort: srt, pageSize: pgSize }
  }

  function getAndSetAppropriateDate(newMoment) {
    let date = newMoment
    if (!date || !dayjs.isDayjs(date) || !date.isValid()) {
      if (reportDate && dayjs.isDayjs(reportDate) && reportDate.isValid()) {
        date = reportDate
      } else {
        try {
          date = safeDayjsTz().startOf('day')
        } catch {
          date = dayjs().startOf('day')
        }
      }
    }
    // Ensure the date is valid before setting
    if (!dayjs.isDayjs(date) || !date.isValid()) {
      try {
        date = safeDayjsTz().startOf('day')
      } catch {
        date = dayjs().startOf('day')
      }
    }
    setReportDate(date)
    return date
  }

  function getAndSetAppropriateEndDate(newMoment) {
    let date

    // If newMoment is provided and is a valid moment object, use it directly
    if (newMoment && dayjs.isDayjs(newMoment) && newMoment.isValid()) {
      date = newMoment
    }
    // If newMoment is not a moment object but could be converted to a valid date
    else if (newMoment && !dayjs.isDayjs(newMoment)) {
      const tempMoment = dayjs(newMoment)
      if (tempMoment.isValid()) {
        date = tempMoment
      }
    }

    // If we still don't have a valid date, fall back to existing reportEndDate or current moment
    if (!date) {
      if (reportEndDate && dayjs.isDayjs(reportEndDate) && reportEndDate.isValid()) {
        date = reportEndDate
      } else {
        // Ultimate fallback to current moment with timezone
        try {
          date = safeDayjsTz().endOf('day')
        } catch {
          date = dayjs().endOf('day')
        }
      }
    }

    setReportEndDate(date)
    return date
  }

  function getAppropriateFilters(apiFilters) {
    let filtrs = apiFilters

    if (!filtrs) {
      filtrs = filters
    }

    return sanitizeFilterQuery(filtrs)
  }

  async function getDataWithSortPageOrSize(
    page,
    sort,
    pageSize,
    newSelectedMoment,
    apiFilters,
    newSelectedEndMoment,
    getSummary = false,
  ) {
    setLoadingPageData(true)

    const paginationData = getAndSetAppropriatePaginationData(page, sort, pageSize)
    const newMoment = getAndSetAppropriateDate(newSelectedMoment)
    const newEndMoment = getAndSetAppropriateEndDate(newSelectedEndMoment)
    const fltrs = getAppropriateFilters(apiFilters)
    let promises = [
      getReports({
        page: paginationData?.page,
        reportDateMoment: newMoment,
        reportEndDateMoment: newEndMoment,
        filterQuery: fltrs,
      }),
    ]
    if (showSummaryReport && getSummary) {
      promises = [
        ...promises,
        getSummaryReports({
          reportDateMoment: newMoment,
          reportEndDateMoment: newEndMoment,
          filterQuery: fltrs,
        }),
      ]
    }
    await Promise.all(promises)

    setLoadingPageData(false)
  }
  function resetFilters() {
    setPatientNumberInput('')
    setFilters(DEFAULT_FILTERS)
  }

  function openBookedEventLogsDialog(bookedEvent) {
    setSelectedBookedEvent(bookedEvent)
    setShowBookedEventLogsDialog(true)
  }

  function closeBookedEventLogsDialog() {
    setShowBookedEventLogsDialog(false)
    setSelectedBookedEvent(null)
  }

  // Function to refresh reports while preserving current date range
  function refreshReportsWithCurrentDateRange() {
    getDataWithSortPageOrSize(0, undefined, undefined, reportDate, undefined, reportEndDate, true)
  }

  useEffect(() => {
    setPatientNumberInput(filters?.['patient-number'] || '')
  }, [filters?.['patient-number']])

  useEffect(() => {
    const trimmedValue = patientNumberInput.trim()
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        const currentValue = prev?.['patient-number'] || ''
        if (!currentValue && !trimmedValue) {
          return prev
        }
        if (currentValue === trimmedValue) {
          return prev
        }

        const nextFilters = { ...(prev || {}) }
        if (trimmedValue) {
          nextFilters['patient-number'] = trimmedValue
        } else {
          delete nextFilters['patient-number']
        }
        if (!nextFilters.eventType) {
          nextFilters.eventType = DEFAULT_EVENT_TYPE
        }
        return nextFilters
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [patientNumberInput])

  useEffect(() => {
    if (!hasInitializedFiltersRef.current) {
      hasInitializedFiltersRef.current = true
      return
    }
    getDataWithSortPageOrSize(0, null, null, reportDate, filters, reportEndDate, true)
  }, [filters])

  useEffect(() => {
    if (!hasInitializedPaymentConfigRef.current) {
      hasInitializedPaymentConfigRef.current = true
      return
    }
    const previousCycleRange = getPreviousPaymentCycleDateRange(paymentConfig)
    getDataWithSortPageOrSize(
      0,
      undefined,
      undefined,
      previousCycleRange.start,
      filters,
      previousCycleRange.end,
      true,
    )
  }, [paymentConfigSignature])

  useEffect(() => {
    getDataWithSortPageOrSize(0, undefined, undefined, reportDate, filters, reportEndDate, true)
  }, [])

  return (
    <>
      {!hideBackButton && (
        <GenericHeaderBackButton
          clickfunction={() => window.history.back()}
          styles={{ margin: '20px' }}
        />
      )}
      <PageMainContentContainer style={{ maxWidth: '1600px', width: '100%' }}>
        <EDVCParent>
          <div className="d-flex flex-column align-items-stretch gap-2 w-100">
            <PayoutReportsToolbar className="w-100">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      <PayoutReportsDateRangeBox>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="From date"
                            value={reportDate}
                            onChange={(newVal) => {
                              if (!newVal?.isValid?.()) {
                                return
                              }
                              const startDate = newVal.startOf('day')
                              const endDate = reportEndDate.isBefore(startDate, 'day')
                                ? startDate
                                : reportEndDate
                              getDataWithSortPageOrSize(
                                0,
                                undefined,
                                undefined,
                                startDate,
                                undefined,
                                endDate,
                                true,
                              )
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { minWidth: 170 },
                              },
                            }}
                          />
                          <DatePicker
                            label="To date"
                            value={reportEndDate}
                            minDate={reportDate}
                            onChange={(newVal) => {
                              if (!newVal?.isValid?.()) {
                                return
                              }
                              getDataWithSortPageOrSize(
                                0,
                                undefined,
                                undefined,
                                reportDate,
                                undefined,
                                newVal.startOf('day'),
                                true,
                              )
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { minWidth: 170 },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </PayoutReportsDateRangeBox>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 700 }}
                          >
                            {usesCalendarMonthCycle ? 'Jump to month' : 'Jump to cycle'}
                          </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {paymentCycleShortcuts.map((shortcut) => {
                            const isActive =
                              reportDate?.isSame?.(shortcut.start, 'day') &&
                              reportEndDate?.isSame?.(shortcut.end, 'day')
                            return (
                              <Chip
                                key={shortcut.key}
                                label={shortcut.label}
                                clickable
                                color={isActive ? 'primary' : 'default'}
                                variant={isActive ? 'filled' : 'outlined'}
                                onClick={() =>
                                  getDataWithSortPageOrSize(
                                    0,
                                    undefined,
                                    undefined,
                                    shortcut.start,
                                    undefined,
                                    shortcut.end,
                                    true,
                                  )
                                }
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <ExportPayoutReportsDialogWithButton
                      dataFetchingApiUrl={reportListApiSignature}
                      startDate={reportDate}
                      endDate={reportEndDate}
                      entityId={reportsListingApiParams?.['teacher-id']}
                      entityType={'mentor'}
                      eventType={activeEventType}
                      allEventTypes={allEventTypes}
                      fileNamePrefix={'Practitioner payout reports'}
                      extraParams={{
                        ...reportsListingApiParams,
                        ...filters,
                        eventType: activeEventType,
                      }}
                      payoutReportDataArrayToExcelFormatArrayBuilder={
                        buildTherapistPayoutReportsExcel
                      }
                      useLocalDateFormatForApiCalls={useLocalDateFormatForApiCalls}
                      paymentConfig={paymentConfig}
                      periodOptions={paymentCycleShortcuts}
                      practitionerContact={practitionerContact}
                    />
                    {showAdminActions && (
                      <>
                        <PaymentConfigMenuWithDialog
                          payoutEntityId={reportsListingApiParams?.['teacher-id']}
                          payoutEntityType={'mentor'}
                          entityName={'Practitioner'}
                          onPaymentConfigSaved={onPaymentConfigSaved}
                        />
                      </>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: !hasLockedClinicBrand
                          ? 'repeat(3, minmax(0, 1fr))'
                          : 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    <CustomSelect
                      options={EVENT_TYPES_ARRAY?.filter(
                        (type) =>
                          type?.id === EVENT_TYPE_THERAPY_SESSION ||
                          type?.id === EVENT_TYPE_SCREENING,
                      )}
                      value={activeEventType}
                      label="Service type"
                      getOptionLabel={(option) => option?.title}
                      getOptionValue={(option) => option?.id}
                      selectType={selectTypes.string}
                      menuPortalTarget={selectMenuPortalTarget}
                      styles={SELECT_MENU_STYLES}
                      onChangeEvent={(e) => updateFilter('eventType', e.target.value)}
                    />
                    {!hasLockedClinicBrand && (
                      <CustomSelect
                        options={clinicBrands}
                        value={filters?.['clinic-brand-id']}
                        label="Clinic brand"
                        getOptionLabel={(option) => option?.title}
                        getOptionValue={(option) => option?.id}
                        selectType={selectTypes.string}
                        isClearable
                        isDisabled={isNonClinicalOnly}
                        placeholder={
                          isNonClinicalOnly
                            ? 'Not applicable for non-clinical bookings'
                            : 'All associated clinic brands'
                        }
                        menuPortalTarget={selectMenuPortalTarget}
                        styles={SELECT_MENU_STYLES}
                        onChangeEvent={(e) => updateFilter('clinic-brand-id', e.target.value)}
                      />
                    )}
                    <CustomInput
                      id="patientNumber"
                      label="Patient number"
                      value={patientNumberInput}
                      onChange={(e) => setPatientNumberInput(e.target.value)}
                      placeholder="Search by patient number"
                      showClearInputButton
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                          Booking scope
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={filters?.nonClinicalEvents ?? 'all'}
                          onChange={(_, value) => {
                            if (value !== null) {
                              updateFilter('nonClinicalEvents', value === 'all' ? null : value)
                            }
                          }}
                          sx={{
                            flexWrap: 'wrap',
                            gap: 0.75,
                            '& .MuiToggleButtonGroup-grouped': {
                              borderRadius: '999px !important',
                              border: '1px solid',
                              borderColor: 'divider',
                              margin: 0,
                              px: 1.5,
                              textTransform: 'none',
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="false">Clinical</ToggleButton>
                          <ToggleButton value="true">Non-clinical</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                          Completion status
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={filters?.markedCompletedEvents ?? 'all'}
                          onChange={(_, value) => {
                            if (value !== null) {
                              updateFilter('markedCompletedEvents', value === 'all' ? null : value)
                            }
                          }}
                          sx={{
                            flexWrap: 'wrap',
                            gap: 0.75,
                            '& .MuiToggleButtonGroup-grouped': {
                              borderRadius: '999px !important',
                              border: '1px solid',
                              borderColor: 'divider',
                              margin: 0,
                              px: 1.5,
                              textTransform: 'none',
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="true">Marked completed</ToggleButton>
                          <ToggleButton value="false">Not marked completed</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Box>
                    {isAnyFilterApplied && (
                      <Button
                        color="inherit"
                        variant="text"
                        startIcon={<RestartAltRoundedIcon />}
                        onClick={resetFilters}
                        sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                      >
                        Reset filters
                      </Button>
                    )}
                  </Box>

                  {activeFilterChips.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {activeFilterChips.map((chip) => (
                        <Chip
                          key={chip.key}
                          label={chip.label}
                          size="small"
                          variant="outlined"
                          onDelete={chip.onDelete}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {showSummaryReport && (
                  <PayoutReportSummary
                    summaryReport={summaryReport}
                    eventType={activeEventType}
                    allEventTypes={allEventTypes}
                    forMentor
                    currentDate={reportDate}
                    previousDate={reportEndDate}
                    loadingSummary={loadingSummaryReport}
                    totalSessionTitle={`Total ${
                      EVENT_TYPES_ARRAY.find((eve) => eve?.id === activeEventType)?.title || ''
                    } Sessions`}
                    forTherapist
                  />
                )}
              </Box>
            </PayoutReportsToolbar>
            {paginationData?.totalPages > 1 && (
              <PayoutReportsPaginationBar className="w-100" sx={{ py: 0 }}>
                <Typography className="pagination-label" component="span">
                  Page {paginationData?.page + 1} of {paginationData?.totalPages}
                </Typography>
                <Pagination
                  count={paginationData?.totalPages}
                  page={paginationData?.page + 1}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  onChange={(_, page) =>
                    getDataWithSortPageOrSize(
                      page - 1,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      false,
                    )
                  }
                  color="primary"
                />
              </PayoutReportsPaginationBar>
            )}
            <BrandPayoutStyledSmartTable>
              <CSmartTable
                items={listData?.map((itm) => {
                  const needsReconsiliation = checkIfReportNeedsReconsiliationForTherapists(itm)
                  return {
                    ...itm,
                    _props: {
                      color: needsReconsiliation
                        ? 'danger'
                        : itm?.reconciliationApplied
                          ? 'warning'
                          : undefined,
                    },
                  }
                })}
                columns={columns({})}
                loading={loadingPageData}
                tableProps={{
                  hover: true,
                  striped: true,
                  responsive: true,
                }}
                // columnSorter
                // pagination
                // onColumnFilterChange={(filter) => {
                //   getDataWithSortPageOrSize(1)
                //   setColumnFilter(filter)
                // }}
                // onItemsPerPageChange={(pageSize) => {
                //   getDataWithSortPageOrSize(1, undefined, pageSize)
                //   setItemsPerPage(pageSize)
                // }}
                itemsPerPage={10000000000000000}
                scopedColumns={scopedColumns({
                  showAdminActions,
                  refreshReports: refreshReportsWithCurrentDateRange,
                  openBookedEventLogsDialog,
                })}
              />
            </BrandPayoutStyledSmartTable>
          </div>
        </EDVCParent>
        {selectedBookedEvent && (
          <BookedEventLogsDrawer
            open={showBookedEventLogsDialog}
            onClose={closeBookedEventLogsDialog}
            bookedSlot={{
              ...selectedBookedEvent,
              id: selectedBookedEvent?.bookedEventId,
              startTime: selectedBookedEvent?.bookedEventStartTime,
              endTime: selectedBookedEvent?.bookedEventEndTime,
              patientInfo: {
                name: selectedBookedEvent?.patientName,
                patientNumber: selectedBookedEvent?.patientNumber,
                gender: selectedBookedEvent?.patientGender,
                dateOfBirth: selectedBookedEvent?.patientDateOfBirth,
                mobile: selectedBookedEvent?.patientMobile,
              },
            }}
            apiEndpoint={bookedEventLogApiUrl}
          />
        )}
      </PageMainContentContainer>
    </>
  )
}

const columns = ({ isForBrand }) => {
  return [
    getCSmartTableColumnData({
      key: 'patientName',
      label: 'Patient Name',
      filter: false,
      sorter: false,
      _style: { minWidth: '250px' },
    }),
    getCSmartTableColumnData({
      key: 'eventDetails',
      label: 'Booking Details',
      filter: false,
      sorter: false,
      _style: { minWidth: '250px' },
    }),
    getCSmartTableColumnData({
      key: 'feeDetails',
      label: 'Fee Details',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'settlementDetails',
      label: 'Mentor Share',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
    getCSmartTableColumnData({
      key: 'view',
      label: 'View',
      filter: false,
      sorter: false,
      _style: { minWidth: '150px' },
    }),
  ]
}
const scopedColumns = ({
  isForBrand,
  propEventType,
  showAdminActions,
  refreshReports,
  openBookedEventLogsDialog,
}) => {
  return {
    collectedBy: (item) => (
      <td>
        <div className="d-flex flex-column">
          <span>
            (
            {item?.paymentMethod ===
            PAYMENT_METHOD_KV_MAP.BOOKING_FEE_PAYMENT_METHOD_COLLECTED_BY_AUMHUM
              ? 'Collected By AUMHUM'
              : 'Collected By Clinic'}
            )
          </span>
          <span>{item?.collectedBy}</span>
          <span>
            {item?.collectedAt
              ? dayjs
                  .utc(item?.collectedAt)
                  .local()
                  .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
              : 'No Data'}
          </span>
        </div>
      </td>
    ),
    clinicBrandDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getSharePercent={(item) => item?.sourceSharePercentage}
            getShareAmount={(item) => item?.sourceShare}
            getEntityTitle={(item) => item?.sourceName}
            getEntityFeeModelDetails={(item) => item?.sourceEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.sourceFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    grossMargin: (item) => (
      <td>
        {isValidNumber(item?.grossMargin)
          ? getPriceStringWithCurrency(item?.grossMargin, item?.currency)
          : 'Cannot Calculate'}
      </td>
    ),
    visitDetails: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <span>Visit Number: {item?.sessionNumOfSameTypeWithSameMentor}</span>
            <span>
              Fee Paid:{' '}
              {isValidNumber(item?.feeCollected)
                ? getPriceStringWithCurrency(item?.feeCollected, item?.currency)
                : 'No Data'}
            </span>
          </div>
        </td>
      )
    },
    eventDetails: (item) => {
      // const foundEventType = EVENT_TYPES_ARRAY.find((type) => item?.eventType === type?.id)
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            {item?.clinicBrandId ? (
              <div>
                Clinic: {item?.sourceName} {item?.clinicLocationName}
              </div>
            ) : (
              'Non Clinical Event'
            )}
            <div>
              <span>
                Scheduled Start Time<strong>: </strong>
                {item?.bookedEventStartTime
                  ? dayjs
                      .utc(item?.bookedEventStartTime)
                      .local()
                      .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
                  : 'No Data'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div>
                {BOOKED_EVENT_MODES?.[item?.mode]?.altTitle ? (
                  <Chip label={BOOKED_EVENT_MODES?.[item?.mode]?.altTitle} />
                ) : (
                  ''
                )}
              </div>
              {item?.markedCompleted ? (
                <Chip label={'Completed'} color="success" />
              ) : (
                <Chip label={'Not Completed'} />
              )}
              {item?.testPatient ? <Chip color="warning" label="Test Booking" /> : ''}
              {item?.reconciliationApplied && (
                <Chip
                  color="warning"
                  label="Reconciled"
                  size="small"
                  title={item?.reconciliationAdjustment?.note || 'This booking has been reconciled'}
                />
              )}
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                {item?.nextVisitDate && <Chip color="success" label={`Next Visit Date Given`} />}
                {isValidNumber(item?.reportContentLength) && (
                  <Chip
                    color={item?.reportContentLength < 50 ? 'warning' : 'default'}
                    label={`Report Content Length: ${item?.reportContentLength} chars`}
                  />
                )}
              </div>
            </div>
            <div>Visit #: {item?.sessionNumOfSameTypeWithSameMentor}</div>
            {/* <div className="flex-shrink-0">
                {item?.markedCompleted ? (
                  <Chip color="success" size="small" label="Session completed" />
                ) : (
                  <Chip color="default" size="small" label="Session not completed" />
                )}
              </div> */}
          </div>
        </td>
      )
    },
    mentorDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getEntityTitle={(item) => item?.mentorName}
            getSharePercent={(item) => item?.mentorSharePercentage}
            getShareAmount={(item) => item?.mentorShare}
            getEntityFeeModelDetails={(item) => item?.mentorEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.mentorFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    feeDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportFeeDetails
            report={item}
            getClinicShare={(item) => item?.mentorShare}
            hideClinicShare
            hideAumhumShare
            hideFeeDataOnZeroPercentage
            getClinicSharePercentage={(item) => item?.mentorSharePercentage}
            getCurrency={(item) => item?.currency}
            forMentor
            getMinPayableValue={(item) => item?.minFeeUsedForCalculations}
          />
        </td>
      )
    },
    startTime: (item) => {
      return (
        <td>
          {item?.bookedEventStartTime
            ? dayjs
                .utc(item?.bookedEventStartTime)
                .local()
                .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
            : 'No Data'}
        </td>
      )
    },
    patientName: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-2">
            <span>{item?.patientNumber}</span>
            <span>{item?.patientName}</span>
            <span>
              {getGenderFromGenderId(item?.patientGender)?.label || 'NA'}/
              {getAgeFromDobWithTime(item?.patientDateOfBirth)}
            </span>
          </div>
        </td>
      )
    },
    settlementDetails: (item) => {
      let shareIsZero =
        isValidNumber(item?.mentorSharePercentage) && item?.mentorSharePercentage === 0
      if (shareIsZero) {
        return <td>NA</td>
      }

      return (
        <td>
          <div>
            {isValidNumber(item?.mentorShare)
              ? getPriceStringWithCurrency(item?.mentorShare, item?.currency)
              : 'No Data'}{' '}
          </div>
          <div>
            (
            {isValidNumber(item?.mentorSharePercentage)
              ? `${item?.mentorSharePercentage}%${
                  isValidNumber(item?.minFeeUsedForCalculations)
                    ? ` of ${getPriceStringWithCurrency(
                        item?.minFeeUsedForCalculations,
                        item?.currency,
                      )} (min agreed)`
                    : ''
                }`
              : 'No Data'}
            )
          </div>
        </td>
      )
    },
    view: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-2">
            <ClinicBrandPayoutReportsViewDetails
              hideFeeDetailsWhenShareZero
              forMentor
              report={item}
              showAdminActions={showAdminActions}
              refreshReports={refreshReports}
              showBookingLogsButton
              openBookedEventLogsDialog={() => openBookedEventLogsDialog(item)}
            />
          </div>
        </td>
      )
    },
  }
}
